import { useTranslation } from 'react-i18next';
import { useLoaderData, useMatches, useOutletContext } from 'react-router';
import { useSiteConfig } from '@cx-web/feature-site-config';
import { getRepubMetaFromRepub } from '@cx-web/utils';

import { type Tipster as TipsterType } from '~/api/tipsters/types';
import { Profile } from '~/components/cxy/cxy.ts';
import { Hero } from '~/components/hero/Hero.tsx';

import type { loader as rootLoader } from '../../../root-loader';

import { Tip } from './components/Tip';
import { Tipster } from './components/Tipster';
import styles from './styles/premium-tipsters.module.css';
import { loader } from './loader';
import { meta } from './meta';
import { type OutletContext } from './types';
import { getTipsterLink } from './utils';

export default function PremiumTipstersLandingPage() {
  const matches = useMatches();
  const rootMatch = matches.find(match => /oddschecker-plus\/root$/.test(match.id));
  const rootLoaderData = rootMatch?.data as typeof rootLoader;
  const { monthlyProducts, napOfTheDay, repub, subscriptionStatus, tipstersData } = useLoaderData<typeof loader>();
  const siteConfig = useSiteConfig();
  const { repubIs, repubPath } = getRepubMetaFromRepub(repub);

  const { t } = useTranslation(['label', 'bettype']);
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const buildTipsLink = (tipster: TipsterType) => {
    const tipsterBaseUrl = getTipsterLink(repub);
    const hasActiveTips = tipster.activeTipsCount >= 1;
    const path = hasActiveTips ? '' : 'history';

    // HERE
    const getLinkText = (count: number) => {
      if (count === 0) return t('CX_PLUS_TIPSTERS_SEE_TIPS');
      if (count === 1) return t('CX_PLUS_TIPSTERS_SEE_1_TIP');
      return t('CX_PLUS_TIPSTERS_SEE_X_TIPS', { Amount: count });
    };

    return {
      href: `${tipsterBaseUrl}/${tipster.slug}${path ? `/${path}` : ''}`,
      text: getLinkText(tipster.activeTipsCount),
    };
  };

  const { currentPath, userHasCancelledSubscription } = useOutletContext<OutletContext>();

  const tipsters = tipstersData;

  return (
    <>
      {monthlyProducts.length > 0 && (
        <Hero
          currentPath={currentPath}
          hasActiveSubscription={
            subscriptionStatus.hasPremiumSubscription || subscriptionStatus.hasEssentialSubscription
          }
          hasCancelledSubscription={userHasCancelledSubscription}
          repub={repub}
          repubPath={repubPath}
        >
          <h1>{t('CX_PLUS_TIPSTERS_TITLE')}</h1>
          {repubIs.GZ ? (
            <h4>{t('CX_PLUS_TIPSTERS_HERO_TEXT')}</h4>
          ) : (
            <>
              <h3>{t('CX_PLUS_TIPSTERS_HERO_TITLE')}</h3>
              <p>{t('CX_PLUS_TIPSTERS_HERO_HELPER_TEXT')}</p>
            </>
          )}
        </Hero>
      )}
      <section className={styles.mainContent} data-testid="results-section">
        <ul className={styles.tipsterList}>
          {tipsters?.map((tipster, index) => (
            <li key={`tip-${index}`}>
              <Tipster
                hitRate={tipster.hitRate}
                last10={tipster.last10}
                napOfTheDay={!!napOfTheDay}
                position={index + 1}
                profileLink={tipster.profileLink}
                repub={repub}
                stats={{
                  roi: tipster.roi,
                  lifetime: tipster.lifetimeProfitLoss,
                  year: tipster.ytdProfitLoss,
                }}
                tipLink={buildTipsLink(tipster)}
                tipsterImage={tipster.image.url}
                tipsterName={tipster.name}
                tipsterSubline={tipster.headline}
                updateDate={tipster.updatedAt}
              />
            </li>
          ))}
        </ul>
      </section>
      {napOfTheDay && (
        <section className={styles.sidebar} data-testid="nap-section">
          <h2 className={styles.sidebarTitle}>{t('CX_PLUS_NAP_OF_DAY')}</h2>
          <Profile
            className={styles.profile}
            image={napOfTheDay.tip.tipster.image.url}
            layout="row"
            name={napOfTheDay.tip.tipster.name}
            profileLink={{
              text: t('CX_PLUS_TIPSTERS_SEE_MORE_TIPS'),
              href: `${getTipsterLink(repub)}/${napOfTheDay.tip.tipster.slug}`,
            }}
          />
          <Tip
            bet={napOfTheDay.bet}
            betDescription={napOfTheDay.tip.eachWay ? 'Each way' : t(napOfTheDay.bet.marketName, { ns: 'bettype' })}
            betName={napOfTheDay.bet.name}
            bookmaker={napOfTheDay.bet.odds ? siteConfig.bookmakers[napOfTheDay.bet.odds.bookmakerCode] : undefined}
            napOfTheDay={true}
            repubIs={repubIs}
            sport={napOfTheDay.tip.tipster.headline}
            stats={{
              odds: napOfTheDay.bet.odds
                ? napOfTheDay.bet.odds.fractional // TODO: Dispaly odds matching the user odds format.
                : napOfTheDay.tip.tipsterOdds, // Display tipsterOdds if there is no market data available.
              stake: napOfTheDay.tip.stake,
              return: napOfTheDay.tip.returns,
            }}
            tipImage={''} // TODO: Add tip image
            tipName={napOfTheDay.tip.title}
            tipster={napOfTheDay.tip.tipster.name}
          />
        </section>
      )}
    </>
  );
}

export { meta, loader };
